/**
 * cdwBundle — 「基本料金に含める」プランの印を読む（LP が json_data.data に入れる）
 *
 *   cdw_bundled      CDW 込み（繁体字・韓国語の LP 予約。2026-10-05。那覇・宮古とも）
 *   noc_bundled      NOC 込み（宮古の繁体字・韓国語。2026-10-21）
 *   location_bundled 下地島空港の送迎費込み（宮古・全言語。2026-10-21）
 *   cruise_pricing   クルーズ船料金表の料金で計算した予約（宮古・平良港。2026-10-22）。
 *                    base_price は backend の料金と違い、平良港の送迎料も込み
 *
 * いずれも base_price にその額を含み、cdw_price / noc_price は 0、
 * 送迎費は comment に「送迎場所 追加料金: ¥」を書かない。別に足すと二重計上になる。
 *   - サイドパネル: 「◯◯込みプラン」と含めた額を表示
 *   - Square 請求書（useSquareInvoice → square_create_invoice.php）: その行を出さず、品名に (CDW) / (CDW・NOC)
 *   - AI エージェントの料金変更（agent/priceChange.ts）: 自動再計算は未対応なので止めて人に回す
 * 正: booking-platform/md/options.md「CDW 込み表示」「宮古の繁体字・韓国語は NOC も込み」/
 *     booking-platform/md/stores.md「下地島空港の送迎費は基本料金に含める」
 */
import { extractJsonDataRecord } from './hotelDelivery';

export const CDW_BUNDLED_KEY = 'cdw_bundled';
export const CDW_BUNDLED_AMOUNT_KEY = 'cdw_bundled_amount';
export const NOC_BUNDLED_KEY = 'noc_bundled';
export const NOC_BUNDLED_AMOUNT_KEY = 'noc_bundled_amount';
export const LOCATION_BUNDLED_KEY = 'location_bundled';
export const LOCATION_BUNDLED_AMOUNT_KEY = 'location_bundled_amount';
export const CRUISE_PRICING_KEY = 'cruise_pricing';

export interface CdwBundle {
  bundled: boolean;
  /** 基本料金に含めた CDW 額（円）。記録が無ければ null */
  amount: number | null;
}

function readBundle(jsonData: unknown, flagKey: string, amountKey: string): CdwBundle {
  const data = extractJsonDataRecord(jsonData);
  const flag = data ? String(data[flagKey] ?? '').trim() : '';
  const bundled = flag === '1' || flag.toLowerCase() === 'true';
  if (!bundled) return { bundled: false, amount: null };
  const n = Number(data?.[amountKey]);
  return { bundled: true, amount: Number.isFinite(n) && n > 0 ? n : null };
}

export function getCdwBundle(jsonData: unknown): CdwBundle {
  return readBundle(jsonData, CDW_BUNDLED_KEY, CDW_BUNDLED_AMOUNT_KEY);
}

/** NOC 込みプラン（宮古の繁体字・韓国語）。base_price に NOC を含み noc_price=0 */
export function getNocBundle(jsonData: unknown): CdwBundle {
  return readBundle(jsonData, NOC_BUNDLED_KEY, NOC_BUNDLED_AMOUNT_KEY);
}

/** 送迎費込み（宮古の下地島空港）。base_price に送迎費を含み、comment に別行の書式を書かない */
export function getLocationBundle(jsonData: unknown): CdwBundle {
  return readBundle(jsonData, LOCATION_BUNDLED_KEY, LOCATION_BUNDLED_AMOUNT_KEY);
}

export const isCdwBundled = (jsonData: unknown): boolean => getCdwBundle(jsonData).bundled;
export const isNocBundled = (jsonData: unknown): boolean => getNocBundle(jsonData).bundled;
export const isLocationBundled = (jsonData: unknown): boolean => getLocationBundle(jsonData).bundled;

/** クルーズ船料金表の料金で計算した予約（宮古・平良港）。正: booking-platform/md/pricing.md「クルーズ船料金表」 */
export const isCruisePriced = (jsonData: unknown): boolean => {
  const data = extractJsonDataRecord(jsonData);
  const flag = data ? String(data[CRUISE_PRICING_KEY] ?? '').trim() : '';
  return flag === '1' || flag.toLowerCase() === 'true';
};

/** 料金の自動再計算ができない「込み」プランか（CDW / NOC / 送迎費 / クルーズ船料金のいずれか） */
export const hasBundledPricing = (jsonData: unknown): boolean =>
  isCdwBundled(jsonData) || isNocBundled(jsonData) || isLocationBundled(jsonData) || isCruisePriced(jsonData);
